"use client";

import { c as _c } from "react/compiler-runtime";
import { useRef, type FC, type PropsWithChildren, type HTMLProps } from "react";
import { useEventListener } from "~/client/hooks";
export interface TouchStartParams {
  /**
   * X-axis start position in pixels
   */
  readonly startX?: number;
  /**
   * Y-axis start position in pixels
   */
  readonly startY?: number;
  /**
   * Start time in milliseconds
   */
  readonly startTime: number;
}
export interface TouchMoveParams {
  /**
   * X-axis delta in pixels
   */
  readonly deltaX: number;
  /**
   * Y-axis delta in pixels
   */
  readonly deltaY: number;
  /**
   * Time in milliseconds
   */
  readonly duration: number;
}
export interface TouchEndParams {
  /**
   * X-axis delta in pixels
   */
  readonly deltaX: number;
  /**
   * Y-axis delta in pixels
   */
  readonly deltaY: number;
  /**
   * Time in milliseconds
   */
  readonly duration: number;
}
interface SwipeableProps {
  readonly onTouchStart?: (delta: TouchStartParams) => void;
  readonly onTouchMove?: (delta: TouchMoveParams) => void;
  readonly onTouchEnd?: (delta: TouchEndParams) => void;
}
export const Swipeable: FC<PropsWithChildren<SwipeableProps & Omit<HTMLProps<HTMLDivElement>, "onTouchStart" | "onTouchMove" | "onTouchEnd">>> = t0 => {
  const $ = _c(15);
  let children;
  let onTouchEnd;
  let onTouchMove;
  let onTouchStart;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = onTouchEnd;
    $[3] = onTouchMove;
    $[4] = onTouchStart;
    $[5] = props;
  } else {
    children = $[1];
    onTouchEnd = $[2];
    onTouchMove = $[3];
    onTouchStart = $[4];
    props = $[5];
  }
  const ref = useRef(null);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const startTimeRef = useRef(0);
  let t1;
  if ($[6] !== onTouchStart) {
    t1 = e => {
      if (!ref.current?.contains(e.target as Node)) {
        return;
      }
      e.preventDefault();
      const startX = e.touches[0]?.clientX;
      const startY = e.touches[0]?.clientY;
      const startTime = Date.now();
      if (!startX || !startY) {
        return;
      }
      startXRef.current = startX;
      startYRef.current = startY;
      startTimeRef.current = startTime;
      onTouchStart?.({
        startX,
        startY,
        startTime
      });
    };
    $[6] = onTouchStart;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  useEventListener("touchstart", t1);
  let t2;
  if ($[8] !== onTouchMove) {
    t2 = e_0 => {
      if (!ref.current?.contains(e_0.target as Node)) {
        return;
      }
      const x = e_0.touches[0]?.clientX;
      const y = e_0.touches[0]?.clientY;
      if (!x || !y) {
        return;
      }
      onTouchMove?.({
        deltaX: x - startXRef.current,
        deltaY: y - startYRef.current,
        duration: Date.now() - startTimeRef.current
      });
    };
    $[8] = onTouchMove;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  useEventListener("touchmove", t2);
  let t3;
  if ($[10] !== onTouchEnd) {
    t3 = e_1 => {
      if (!ref.current?.contains(e_1.target as Node)) {
        return;
      }
      e_1.preventDefault();
      const endX = e_1.changedTouches[0]?.clientX;
      const endY = e_1.changedTouches[0]?.clientY;
      const endTime = Date.now();
      if (!endX || !endY) {
        return;
      }
      const deltaX = endX - startXRef.current;
      const deltaY = endY - startYRef.current;
      const duration = endTime - startTimeRef.current;
      onTouchEnd?.({
        deltaX,
        deltaY,
        duration
      });
    };
    $[10] = onTouchEnd;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  useEventListener("touchend", t3);
  let t4;
  if ($[12] !== children || $[13] !== props) {
    t4 = <div ref={ref} data-swipeable={true} {...props}>{children}</div>;
    $[12] = children;
    $[13] = props;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
};